import { useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppEnvironment } from '@/hooks/AppEnvironmentContext';
import { isPhoneLike } from '@/lib/phone';
import { submitServiceRequest } from '@/lib/serviceRequests';

interface WidgetForm {
  fullName: string;
  phone: string;
  email: string;
  requestText: string;
}

const initialForm: WidgetForm = {
  fullName: '',
  phone: '',
  email: '',
  requestText: '',
};

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function SupportWidget() {
  const { isTelegram } = useAppEnvironment();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<WidgetForm>(initialForm);
  const [touched, setTouched] = useState<Record<keyof WidgetForm, boolean>>({
    fullName: false,
    phone: false,
    email: false,
    requestText: false,
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const fullNameValid = form.fullName.trim().length >= 2;
  const phoneValid = isPhoneLike(form.phone);
  const emailValid = isEmail(form.email);
  const isValid = fullNameValid && phoneValid && emailValid;

  const setField = (field: keyof WidgetForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (status !== 'idle') {
      setStatus('idle');
      setError(null);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched({ fullName: true, phone: true, email: true, requestText: true });
    if (!isValid || status === 'submitting') return;

    setStatus('submitting');
    setError(null);
    try {
      await submitServiceRequest({
        request_type: 'consultation',
        source: isTelegram ? 'telegram' : 'web',
        page_slug: 'support-widget',
        page_title: 'Виджет помощи',
        full_name: form.fullName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        request_text: form.requestText.trim() || null,
        meta: {
          path: location.pathname,
          entry_point: 'support_widget',
          entry_point_label: 'Виджет помощи',
        },
      });
      setForm(initialForm);
      setTouched({ fullName: false, phone: false, email: false, requestText: false });
      setStatus('success');
    } catch (submitError) {
      setStatus('error');
      setError(submitError instanceof Error ? submitError.message : 'Не удалось отправить заявку');
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 pb-[env(safe-area-inset-bottom)] md:bottom-6 md:right-6">
      {open && (
        <div className="w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-3xl border border-honey/25 bg-card shadow-hover">
          <div className="flex items-start justify-between gap-4 border-b border-sand/60 px-5 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-honey-dark">Помощь</p>
              <h2 className="mt-1 font-display text-xl font-bold text-dark">Оставить заявку</h2>
              <p className="mt-1 text-sm leading-5 text-dark/55">Ответим в мессенджере или свяжемся по телефону.</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-dark/45 transition-colors hover:bg-sand/50 hover:text-dark"
              aria-label="Закрыть виджет"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          <div className="max-h-[min(76vh,38rem)] overflow-y-auto px-5 py-4">
            <form onSubmit={submit}>
              <div className="grid gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-dark/65">ФИО</span>
                  <input
                    value={form.fullName}
                    onChange={(event) => setField('fullName', event.target.value)}
                    onBlur={() => setTouched((current) => ({ ...current, fullName: true }))}
                    className={`mt-1.5 w-full rounded-2xl border bg-cream px-4 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-honey/30 ${
                      touched.fullName && !fullNameValid ? 'border-red-300' : 'border-sand/60'
                    }`}
                    autoComplete="name"
                    placeholder="Иван Иванов"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium text-dark/65">Телефон</span>
                  <input
                    value={form.phone}
                    onChange={(event) => setField('phone', event.target.value)}
                    onBlur={() => setTouched((current) => ({ ...current, phone: true }))}
                    className={`mt-1.5 w-full rounded-2xl border bg-cream px-4 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-honey/30 ${
                      touched.phone && !phoneValid ? 'border-red-300' : 'border-sand/60'
                    }`}
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="+7 900 000 00 00"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium text-dark/65">Email</span>
                  <input
                    value={form.email}
                    onChange={(event) => setField('email', event.target.value)}
                    onBlur={() => setTouched((current) => ({ ...current, email: true }))}
                    className={`mt-1.5 w-full rounded-2xl border bg-cream px-4 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-honey/30 ${
                      touched.email && !emailValid ? 'border-red-300' : 'border-sand/60'
                    }`}
                    autoComplete="email"
                    inputMode="email"
                    placeholder="mail@example.com"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium text-dark/65">Комментарий</span>
                  <textarea
                    value={form.requestText}
                    onChange={(event) => setField('requestText', event.target.value)}
                    className="mt-1.5 min-h-24 w-full resize-none rounded-2xl border border-sand/60 bg-cream px-4 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-honey/30"
                    placeholder="Коротко опишите вопрос"
                  />
                </label>
              </div>

              {status === 'success' && (
                <div className="mt-3 rounded-2xl bg-nature/10 px-4 py-3 text-sm font-medium text-nature">
                  Заявка отправлена. Скоро свяжемся с вами.
                </div>
              )}
              {status === 'error' && (
                <div className="mt-3 rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-honey px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-honey-dark disabled:cursor-not-allowed disabled:bg-honey/50"
              >
                <span>{status === 'submitting' ? 'Отправляем...' : 'Отправить заявку'}</span>
                <i className="ri-arrow-right-line text-base" />
              </button>
            </form>

            <div className="mt-4 border-t border-sand/60 pt-4">
              <p className="text-xs font-medium text-dark/45">Написать напрямую</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <a
                  href="https://t.me/ecoacademybot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-nature/10 px-4 py-2.5 text-sm font-medium text-nature transition-colors hover:bg-nature/15"
                >
                  <i className="ri-telegram-line text-base" />
                  Telegram
                </a>
                <a
                  href="https://vk.com/nil18"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-honey/10 px-4 py-2.5 text-sm font-medium text-honey-dark transition-colors hover:bg-honey/15"
                >
                  <i className="ri-vk-line text-base" />
                  VK
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex items-center gap-2 rounded-full bg-honey px-4 py-3 text-sm font-semibold text-white shadow-hover transition-colors hover:bg-honey-dark md:px-5"
        aria-expanded={open}
      >
        <i className={open ? 'ri-close-line text-lg' : 'ri-chat-1-line text-lg'} />
        <span>Нужна помощь?</span>
      </button>
    </div>
  );
}
